import { useState, useEffect, useRef, useCallback } from "react";
import { Car, CreditCard, Loader2, AlertCircle, CheckCircle, Clock, QrCode, Smartphone } from "lucide-react";
import axios from "axios";
import QRCode from "qrcode";

const API = '/api';
const RADISSON_SITE_ID = "site_621702f1";
const POLL_INTERVAL = 3000; // Poll every 3 seconds
const MAX_POLL_TIME = 900000; // 15 minutes max polling time

/**
 * RadissonPay - Dedicated Radisson QR Payment Kiosk Page
 * 
 * Route: /radisson/pay
 * 
 * Purpose: Kiosk payment page for Radisson Blu Hotel car park.
 * Customer selects a product, scans QR with phone to pay via Stripe.
 * 
 * Flow:
 * 1. Customer enters VRM
 * 2. GET /api/parking/tariff/{vrm}?site_id=site_621702f1
 * 3. Display Radisson product choices
 * 4. Customer selects product
 * 5. POST /api/qr/session with selected_product
 * 6. Display QR code (DO NOT redirect to Stripe)
 * 7. Poll /api/qr/status/{checkout_session_id}
 * 8. Show success when payment completed
 * 
 * MUST NOT:
 * - Accept arbitrary site_id from URL (hardcoded to Radisson)
 * - Use /api/payment/mobile/create
 * - Redirect kiosk browser to Stripe
 * - Calculate prices locally (backend is authoritative)
 * - Create exit authority or barrier actions
 */

// Radisson products - display info only, backend is authoritative for pricing/eligibility
const RADISSON_PRODUCTS = [
  { id: "1_hour", name: "1 Hour", price: 4.00, minutes: 60, guestOnly: false },
  { id: "2_hours", name: "2 Hours", price: 8.00, minutes: 120, guestOnly: false },
  { id: "4_hours", name: "4 Hours", price: 16.00, minutes: 240, guestOnly: false },
  { id: "day", name: "Day", price: 15.00, minutes: 540, guestOnly: false, note: "09:30–18:30 only" },
  { id: "1_night", name: "1 Night", price: 30.00, minutes: 1440, guestOnly: true },
  { id: "2_nights", name: "2 Nights", price: 60.00, minutes: 2880, guestOnly: true },
  { id: "3_nights", name: "3 Nights", price: 90.00, minutes: 4320, guestOnly: true },
];

export default function RadissonPay() {
  // VRM entry state
  const [vrm, setVrm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tariffData, setTariffData] = useState(null);
  
  // Product selection state
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // QR payment state
  const [qrLoading, setQrLoading] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
  const [checkoutData, setCheckoutData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle, pending, succeeded, failed, expired
  
  const pollIntervalRef = useRef(null);
  const pollStartTimeRef = useRef(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // Check tariff for Radisson site only
  const handleCheckFee = async () => {
    const cleanVrm = vrm.trim().toUpperCase().replace(/\s/g, "");
    if (!cleanVrm || cleanVrm.length < 4) {
      setError("Please enter a valid vehicle registration");
      return;
    }

    setLoading(true);
    setError(null);
    setTariffData(null);
    setSelectedProduct(null);
    setQrCodeDataUrl(null);
    setCheckoutData(null);
    setPaymentStatus('idle');

    try {
      // Always scope to Radisson site
      const response = await axios.get(
        `${API}/parking/tariff/${encodeURIComponent(cleanVrm)}?site_id=${encodeURIComponent(RADISSON_SITE_ID)}`
      );
      const data = response.data;

      if (!data.found) {
        setError(data.message || "No active parking session found at this location for this registration.");
        return;
      }

      setTariffData(data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("No active parking session found at this location for this registration.");
      } else {
        setError("Unable to check your parking. Please try again or contact reception.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Create QR payment session
  const handleCreatePayment = async () => {
    if (!tariffData?.session_id || !selectedProduct) {
      setError("Please select a parking product");
      return;
    }

    setQrLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/qr/session`, {
        session_id: tariffData.session_id,
        origin_url: window.location.origin,
        selected_product: selectedProduct.id
      });

      if (response.data && response.data.checkout_url) {
        setCheckoutData(response.data);
        setPaymentStatus('pending');

        // Generate QR code from checkout URL
        const qrDataUrl = await QRCode.toDataURL(
          response.data.qr_data || response.data.checkout_url, 
          {
            width: 300,
            margin: 2,
            color: { dark: '#000000', light: '#ffffff' }
          }
        );
        setQrCodeDataUrl(qrDataUrl);

        // Start polling for payment status
        pollStartTimeRef.current = Date.now();
        startPolling(response.data.checkout_session_id);
      } else {
        throw new Error("Failed to create payment session");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        setError(detail);
      } else {
        setError("Unable to create payment. Please try again or contact reception.");
      }
    } finally {
      setQrLoading(false);
    }
  };

  // Poll for payment status
  const startPolling = useCallback((checkoutSessionId) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = setInterval(async () => {
      // Check if we've exceeded max poll time
      if (Date.now() - pollStartTimeRef.current > MAX_POLL_TIME) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setPaymentStatus('expired');
        setError("Payment session expired. Please try again.");
        return;
      }

      try {
        const statusRes = await axios.get(`${API}/qr/status/${checkoutSessionId}`);
        const data = statusRes.data;

        if (data.payment_status === 'completed' || data.status === 'completed') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setPaymentStatus('succeeded');
        } else if (data.payment_status === 'expired' || data.status === 'expired') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setPaymentStatus('expired');
          setError("Payment session expired. Please try again.");
        } else if (data.payment_status === 'cancelled' || data.status === 'cancelled') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setPaymentStatus('failed');
          setError("Payment was cancelled.");
        } else if (data.is_settled && data.has_exit_authorisation) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setPaymentStatus('succeeded');
        }
      } catch (err) {
        // Continue polling on single error
        console.error("Poll error:", err);
      }
    }, POLL_INTERVAL);
  }, []);

  // Reset to start over
  const handleReset = () => {
    setVrm("");
    setTariffData(null);
    setSelectedProduct(null);
    setQrCodeDataUrl(null);
    setCheckoutData(null);
    setPaymentStatus('idle');
    setError(null);
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "0m";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  return (
    <div
      data-testid="radisson-pay-page"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        padding: "24px",
        boxSizing: "border-box"
      }}
    >
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        <h1 style={{
          fontSize: "28px",
          fontWeight: "700",
          color: "white",
          margin: "0 0 8px 0"
        }}>
          Radisson Blu Parking
        </h1>
        <p style={{ fontSize: "14px", color: "#94a3b8", margin: 0 }}>
          London Canary Wharf East
        </p>
      </div>

      {/* Payment Success State */}
      {paymentStatus === 'succeeded' && (
        <div
          data-testid="payment-success"
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "32px",
            textAlign: "center",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
          }}
        >
          <div style={{
            width: "80px",
            height: "80px",
            background: "#22c55e",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px"
          }}>
            <CheckCircle size={48} color="white" />
          </div>
          <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#22c55e", margin: "0 0 12px" }}>
            Payment Complete
          </h2>
          <p style={{ color: "#64748b", fontSize: "16px", margin: "0 0 8px" }}>
            {tariffData?.vrm}
          </p>
          <p style={{ color: "#374151", fontSize: "18px", margin: "0 0 24px" }}>
            You may now exit the car park
          </p>
          <button
            onClick={handleReset}
            data-testid="new-payment-button"
            style={{
              padding: "14px 32px",
              borderRadius: "10px",
              border: "none",
              background: "#1e3a5f",
              color: "white",
              fontSize: "16px",
              fontWeight: "600",
              cursor: "pointer"
            }}
          >
            New Payment
          </button>
        </div>
      )}

      {/* QR Payment Display */}
      {paymentStatus === 'pending' && qrCodeDataUrl && checkoutData && (
        <div
          data-testid="qr-payment-display"
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            textAlign: "center",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
          }}
        >
          <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b", margin: "0 0 8px" }}>
            Scan to Pay
          </h2>
          <p style={{ color: "#64748b", fontSize: "14px", margin: "0 0 16px" }}>
            Use your phone camera to scan this QR code
          </p>

          {/* QR Code */}
          <div style={{
            background: "white",
            padding: "16px",
            borderRadius: "12px",
            display: "inline-block",
            marginBottom: "16px",
            border: "2px solid #e5e7eb"
          }}>
            <img
              src={qrCodeDataUrl}
              alt="Payment QR Code"
              data-testid="payment-qr-code"
              style={{ width: "240px", height: "240px" }}
            />
          </div>

          {/* Payment Details */}
          <div style={{
            background: "#f8fafc",
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "16px"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <span style={{ color: "#64748b" }}>Registration</span>
              <span style={{ fontWeight: "600", color: "#1e293b" }}>{tariffData?.vrm}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <span style={{ color: "#64748b" }}>Product</span>
              <span style={{ fontWeight: "600", color: "#1e293b" }}>{selectedProduct?.name}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#64748b" }}>Amount</span>
              <span style={{ fontWeight: "700", color: "#16a34a", fontSize: "18px" }}>
                £{(checkoutData.amount / 100).toFixed(2)}
              </span>
            </div>
          </div>

          {/* Instructions */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            color: "#64748b",
            fontSize: "13px"
          }}>
            <Smartphone size={16} />
            <span>Complete payment on your phone</span>
          </div>

          <div style={{
            marginTop: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            color: "#94a3b8",
            fontSize: "12px"
          }}>
            <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
            <span>Waiting for payment...</span>
          </div>

          <button
            onClick={handleReset}
            style={{
              marginTop: "20px",
              padding: "10px 24px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              background: "white",
              color: "#64748b",
              fontSize: "14px",
              cursor: "pointer"
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Error State */}
      {error && paymentStatus !== 'pending' && (
        <div
          data-testid="error-message"
          style={{
            background: "rgba(239, 68, 68, 0.1)",
            border: "1px solid #ef4444",
            borderRadius: "12px",
            padding: "14px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "10px"
          }}
        >
          <AlertCircle size={20} color="#ef4444" />
          <span style={{ color: "#ef4444", fontSize: "14px" }}>{error}</span>
        </div>
      )}

      {/* VRM Entry & Product Selection - only show if not in QR/success state */}
      {paymentStatus !== 'pending' && paymentStatus !== 'succeeded' && (
        <>
          {/* VRM Input Card */}
          <div style={{
            background: "white",
            borderRadius: "16px",
            padding: "20px",
            marginBottom: "16px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)"
          }}>
            <label style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px"
            }}>
              Vehicle Registration
            </label>

            {/* UK Number Plate Style Input */}
            <div style={{
              background: "#fef08a",
              borderRadius: "8px",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              border: "3px solid #eab308",
              marginBottom: "16px"
            }}>
              <div style={{
                background: "#1e40af",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: "700"
              }}>GB</div>
              <input
                type="text"
                value={vrm}
                onChange={(e) => {
                  setVrm(e.target.value.toUpperCase());
                  setError(null);
                  setTariffData(null);
                  setSelectedProduct(null);
                }}
                placeholder="AB12 CDE"
                maxLength={8}
                data-testid="vrm-input"
                style={{
                  flex: 1,
                  border: "none",
                  background: "transparent",
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#1e293b",
                  textAlign: "center",
                  letterSpacing: "4px",
                  fontFamily: "monospace",
                  outline: "none"
                }}
              />
            </div>

            {/* Check Fee Button */}
            <button
              onClick={handleCheckFee}
              disabled={loading || vrm.length < 4}
              data-testid="check-fee-button"
              style={{
                width: "100%",
                padding: "14px",
                borderRadius: "10px",
                border: "none",
                background: loading || vrm.length < 4 ? "#94a3b8" : "#1e3a5f",
                color: "white",
                fontSize: "16px",
                fontWeight: "600",
                cursor: loading || vrm.length < 4 ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px"
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} />
                  Checking...
                </>
              ) : (
                <>
                  <Car size={20} />
                  Check Parking
                </>
              )}
            </button>
          </div>

          {/* Session Found - Show Products */}
          {tariffData && !tariffData.is_settled && (
            <div
              data-testid="product-selection"
              style={{
                background: "white",
                borderRadius: "16px",
                padding: "20px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.2)"
              }}
            >
              {/* Session Info */}
              <div style={{ marginBottom: "16px", paddingBottom: "16px", borderBottom: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ color: "#64748b", fontSize: "14px" }}>Registration</span>
                  <span style={{ fontWeight: "700", color: "#1e293b" }}>{tariffData.vrm}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#64748b", fontSize: "14px" }}>Duration</span>
                  <span style={{ fontWeight: "600", color: "#1e293b", display: "flex", alignItems: "center", gap: "6px" }}>
                    <Clock size={16} color="#64748b" />
                    {formatDuration(tariffData.duration_minutes)}
                  </span>
                </div>
              </div>

              <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#374151", margin: "0 0 12px" }}>
                Select Parking Duration
              </h3>

              {/* Product Grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "16px" }}>
                {RADISSON_PRODUCTS.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => setSelectedProduct(product)}
                    data-testid={`product-${product.id}`}
                    style={{
                      padding: "14px 12px",
                      borderRadius: "10px",
                      border: selectedProduct?.id === product.id ? "2px solid #3b82f6" : "2px solid #e5e7eb",
                      background: selectedProduct?.id === product.id ? "#eff6ff" : "white",
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "all 0.2s"
                    }}
                  >
                    <div style={{ fontWeight: "600", color: "#1e293b", fontSize: "14px" }}>
                      {product.name}
                      {product.guestOnly && (
                        <span style={{ fontSize: "10px", color: "#f59e0b", marginLeft: "4px" }}>Guest</span>
                      )}
                    </div>
                    <div style={{ fontWeight: "700", color: "#16a34a", fontSize: "18px" }}>
                      £{product.price.toFixed(2)}
                    </div>
                    {product.note && (
                      <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "2px" }}>
                        {product.note}
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Pay Button */}
              <button
                onClick={handleCreatePayment}
                disabled={!selectedProduct || qrLoading}
                data-testid="create-payment-button"
                style={{
                  width: "100%",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "none",
                  background: !selectedProduct || qrLoading 
                    ? "#94a3b8" 
                    : "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                  color: "white",
                  fontSize: "18px",
                  fontWeight: "700",
                  cursor: !selectedProduct || qrLoading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  boxShadow: selectedProduct && !qrLoading ? "0 4px 12px rgba(22, 163, 74, 0.3)" : "none"
                }}
              >
                {qrLoading ? (
                  <>
                    <Loader2 size={22} style={{ animation: "spin 1s linear infinite" }} />
                    Creating Payment...
                  </>
                ) : (
                  <>
                    <QrCode size={22} />
                    {selectedProduct ? `Pay £${selectedProduct.price.toFixed(2)}` : "Select a Product"}
                  </>
                )}
              </button>

              <p style={{ textAlign: "center", color: "#94a3b8", fontSize: "12px", margin: "12px 0 0" }}>
                Scan QR code with your phone to complete payment
              </p>
            </div>
          )}

          {/* Already Paid State */}
          {tariffData && tariffData.is_settled && (
            <div style={{
              background: "white",
              borderRadius: "16px",
              padding: "24px",
              textAlign: "center",
              boxShadow: "0 4px 20px rgba(0,0,0,0.2)"
            }}>
              <div style={{
                width: "64px",
                height: "64px",
                background: "#22c55e",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px"
              }}>
                <CheckCircle size={32} color="white" />
              </div>
              <h3 style={{ fontSize: "20px", fontWeight: "700", color: "#22c55e", margin: "0 0 8px" }}>
                Already Paid
              </h3>
              <p style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>
                Your parking session is paid. You may exit the car park.
              </p>
            </div>
          )}
        </>
      )}

      {/* Help Footer */}
      <div style={{
        textAlign: "center",
        marginTop: "24px",
        color: "#94a3b8",
        fontSize: "13px"
      }}>
        <p style={{ margin: "0 0 4px" }}>Need help? Contact reception.</p>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
